/**
 * BaseGrid - A helper class for creating GridJS tables with common functionality
 * 
 * Provides shared functionality for all grid controllers:
 * - Server-side pagination with URL sync
 * - Skeleton loader integration
 * - Bootstrap tooltip initialization
 * - Consistent styling and className configuration
 * 
 * Usage:
 *   import BaseGrid from "../utils/base_grid"
 * 
 *   class MyGridController extends Controller {
 *     static values = { url: String, translations: Object }
 *     static targets = ["container", "skeleton"]
 * 
 *     connect() {
 *       this.baseGrid = new BaseGrid(this, {
 *         columns: [...],
 *         mapData: (item) => [item.name, item.type, ...],
 *         limit: 25
 *       })
 *       this.grid = this.baseGrid.create()
 *     }
 * 
 *     disconnect() {
 *       this.baseGrid.destroy()
 *     }
 *   }
 */

import { Grid, html } from "gridjs"
import { Handlebars } from "./handlebars_helpers"
import { enableTopPagination } from "./grid_helpers"
import { hideGridSkeleton, setupGridSkeleton } from "./gridjs_skeleton"
import { activateTooltips } from "./tooltips"

export default class BaseGrid {
  /**
   * @param {Controller} controller - The Stimulus controller instance
   * @param {Object} options - Grid configuration options
   * @param {Array} options.columns - GridJS column definitions
   * @param {Function} options.mapData - Function to map API response item to row array
   * @param {number} [options.limit=25] - Items per page
   * @param {boolean} [options.search=false] - Enable GridJS built-in search
   * @param {boolean} [options.sort=true] - Enable sorting
   * @param {Object} [options.className] - Custom className overrides
   * @param {Function} [options.onDataLoad] - Callback after data loads (receives data)
   * @param {Function} [options.onReady] - Callback when grid is ready
   * @param {Function} [options.onRender] - Callback after each render
   */
  constructor(controller, options) {
    this.controller = controller
    this.options = options
    this.grid = null
  }

  /**
   * Get the URL value from the controller
   */
  get urlValue() {
    return this.controller.urlValue
  }

  /**
   * Get translations from the controller
   */
  get translations() {
    return this.controller.translationsValue || {}
  }

  /**
   * Check if controller has skeleton target
   */
  get hasSkeletonTarget() {
    return this.controller.hasSkeletonTarget
  }

  /**
   * Get skeleton target element
   */
  get skeletonTarget() {
    return this.controller.skeletonTarget
  }

  /**
   * Get container target element
   */
  get containerTarget() {
    return this.controller.containerTarget
  }

  /**
   * Build the initial URL with current query parameters
   */
  buildInitialUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const initialUrl = new URL(this.urlValue, window.location.origin)
    urlParams.forEach((value, key) => {
      initialUrl.searchParams.set(key, value)
    })
    return initialUrl.toString()
  }

  /**
   * Get the initial page number from URL (0-indexed for GridJS)
   */
  getInitialPage() {
    const urlParams = new URLSearchParams(window.location.search)
    return parseInt(urlParams.get("page") || 1) - 1
  }

  /**
   * Default className configuration for dark theme
   */
  getDefaultClassName() {
    return {
      table: 'table data-table',
      th: 'bg-dark text-light',
      td: 'bg-dark text-light',
      footer: 'bg-dark text-light',
      ...this.options.className
    }
  }

  /**
   * Default style configuration
   */
  getDefaultStyle() {
    return {
      table: {
        'width': '100%'
      },
      ...this.options.style
    }
  }

  /**
   * Server configuration with skeleton integration
   */
  getServerConfig() {
    return {
      url: this.buildInitialUrl(),
      then: (data) => {
        // Hide skeleton after data loads
        setTimeout(() => {
          if (this.hasSkeletonTarget) {
            hideGridSkeleton(this.skeletonTarget, this.containerTarget)
          } else {
            this.containerTarget.style.display = ''
          }
        }, 50)

        // Call optional onDataLoad callback
        if (this.options.onDataLoad) {
          this.options.onDataLoad(data)
        }

        // Map data using the provided mapData function
        return data.data.map(this.options.mapData)
      },
      total: (data) => data.meta?.total || data.data?.length || 0
    }
  }

  /**
   * Pagination configuration with server-side support
   */
  getPaginationConfig() {
    const limit = this.options.limit || 25

    return {
      enabled: true,
      limit: limit,
      page: this.getInitialPage(),
      server: {
        url: (prev, page, limit) => {
          const separator = prev.includes("?") ? "&" : "?"
          return `${prev}${separator}page=${page + 1}&per_page=${limit}`
        }
      }
    }
  }

  /**
   * Initialize Bootstrap tooltips within the grid container
   */
  initTooltips() {
    activateTooltips(this.containerTarget)
  }

  /**
   * Create and render the grid
   * @returns {Grid} The GridJS instance
   */
  create() {
    this.grid = new Grid({
      columns: this.options.columns,
      server: this.getServerConfig(),
      pagination: this.getPaginationConfig(),
      search: this.options.search || false,
      sort: this.options.sort !== false,
      loading: false, // We use skeleton instead
      className: this.getDefaultClassName(),
      style: this.getDefaultStyle(),
      language: this.options.language
    }).render(this.containerTarget)

    // Enable top pagination
    enableTopPagination(this.grid, this.containerTarget)

    // Setup skeleton for pagination support
    if (this.hasSkeletonTarget) {
      setupGridSkeleton(this.grid, this.skeletonTarget, this.containerTarget)
    }

    // Initialize tooltips on ready
    this.grid.on('ready', () => {
      this.initTooltips()
      if (this.options.onReady) {
        this.options.onReady()
      }
    })

    // Re-initialize tooltips on render
    this.grid.on('render', () => {
      setTimeout(() => {
        this.initTooltips()
        if (this.options.onRender) {
          this.options.onRender()
        }
      }, 100)
    })

    return this.grid
  }

  /**
   * Update the grid with new server URL and re-render
   * @param {string} url - New URL to fetch data from
   * @param {Object} [paginationOverrides] - Override pagination settings
   */
  updateUrl(url, paginationOverrides = {}) {
    this.grid.updateConfig({
      server: {
        ...this.getServerConfig(),
        url: url
      },
      pagination: {
        ...this.getPaginationConfig(),
        page: 0, // Reset to first page by default
        ...paginationOverrides
      }
    }).forceRender()
  }

  /**
   * Update the grid with new filter parameters
   * Handles URL construction, grid update, and browser history
   * @param {Object} newParams - Key-value pairs of filter parameters
   * @param {boolean} [updateBrowserUrl=true] - Whether to update browser URL
   */
  updateWithFilters(newParams, updateBrowserUrl = true) {
    const currentUrl = new URL(this.urlValue, window.location.origin)
    const urlParams = new URLSearchParams(window.location.search)
    
    // Merge current URL params (except page)
    urlParams.forEach((value, key) => {
      if (key !== 'page') {
        currentUrl.searchParams.set(key, value)
      }
    })
    
    // Apply new params
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        currentUrl.searchParams.set(key, value)
      } else {
        currentUrl.searchParams.delete(key)
      }
    })

    // Update grid with new URL
    this.grid.updateConfig({
      server: {
        url: currentUrl.toString(),
        then: (data) => {
          if (this.options.onDataLoad) {
            this.options.onDataLoad(data)
          }
          return data.data.map(this.options.mapData)
        },
        total: (data) => data.meta?.total || data.data?.length || 0
      },
      pagination: {
        page: 0
      }
    }).forceRender()

    // Update browser URL if requested
    if (updateBrowserUrl) {
      const browserUrl = new URL(window.location.href)
      Object.entries(newParams).forEach(([key, value]) => {
        if (value) {
          browserUrl.searchParams.set(key, value)
        } else {
          browserUrl.searchParams.delete(key)
        }
      })
      browserUrl.searchParams.delete('page')
      window.history.pushState({}, '', browserUrl.toString())
    }
  }

  /**
   * Destroy the grid instance
   */
  destroy() {
    if (this.grid) {
      this.grid.destroy()
      this.grid = null
    }
  }
}

// Export html helper and Handlebars for use in column formatters
export { html, Handlebars }
